import tokens from '@lansforsakringar/core-tokens'
import { css } from 'lit'

import { vars, scoped, declare, define, override } from '#utils/styles'
import { locals as button } from '#components/button/button/lfds-button.css.ts'
import { locals as heading } from '#components/typography/heading/lfds-heading.css.ts'

export const locals = scoped('alert', [
	'color',
	'background_color',
	'border_color'
] as const)

export default css`
	:host {
		${declare(locals, {
			color: tokens.color_text_primary,
			background_color: tokens.color_background_info,
			border_color: tokens.color_border_on_info
		})};

		display: block;
		width: 100%;
		container-type: inline-size;
	}

	dialog.wrapper {
		position: static;
	}

	.wrapper {
		box-sizing: border-box;
		display: grid;
		min-height: 3.5rem;
		width: 100%;
		column-gap: ${vars(tokens.spacing_sm)};
		align-items: center;
		padding-inline: ${vars(tokens.spacing_lg)};
		padding-block: ${vars(tokens.spacing_lg)};
		border-radius: ${vars(tokens.border_radius_sm)};
		color: ${vars(locals.color)};
		background-color: ${vars(locals.background_color)};
		border: ${vars(tokens.border_width_default)} solid
			${vars(locals.border_color)};

		@container (width > 27.5rem) {
			column-gap: ${vars(tokens.spacing_lg)};
			padding-block: ${vars(tokens.spacing_sm)};
		}

		&.info {
			${override(
				locals.color,
				locals.color,
				tokens.component_alert_color_on_info
			)};
			${override(
				locals.background_color,
				locals.background_color,
				tokens.color_background_info
			)};
			${override(
				locals.border_color,
				locals.border_color,
				tokens.color_border_on_info
			)};
		}

		&.warning {
			${override(
				locals.color,
				locals.color,
				tokens.component_alert_color_on_warning
			)};
			${override(
				locals.background_color,
				locals.background_color,
				tokens.color_background_warning
			)};
			${override(
				locals.border_color,
				locals.border_color,
				tokens.color_border_on_warning
			)};
		}

		&.error {
			${override(
				locals.color,
				locals.color,
				tokens.component_alert_color_on_error
			)};
			${override(
				locals.background_color,
				locals.background_color,
				tokens.color_background_error
			)};
			${override(
				locals.border_color,
				locals.border_color,
				tokens.color_border_on_error
			)};
		}

		&.success {
			${override(
				locals.color,
				locals.color,
				tokens.component_alert_color_on_success
			)};
			${override(
				locals.background_color,
				locals.background_color,
				tokens.color_background_success
			)};
			${override(
				locals.border_color,
				locals.border_color,
				tokens.color_border_on_success
			)};
		}

		&.alert {
			@container (width <= 27.5rem) {
				grid-template-columns: auto 1fr auto;
				grid-template-rows: min-content auto auto;
				grid-template-areas:
					'icon content close'
					'button button button';
			}

			@container (width > 27.5rem) {
				grid-template-columns: auto 1fr auto auto;
				grid-template-areas: 'icon content button close';
				min-height: 5rem;
			}
		}

		&.info-box {
			@container (width <= 27.5rem) {
				grid-template-columns: auto 1fr;
				grid-template-rows: min-content auto auto;
				grid-template-areas:
					'icon content'
					'button button';
			}

			@container (width > 27.5rem) {
				grid-template-columns: auto 1fr auto;
				grid-template-areas: 'icon content button';
				min-height: 5rem;
			}
		}

		*:not(::slotted(*)) {
			box-sizing: inherit;
			margin: 0;
			padding: 0;
		}
	}

	.icon {
		grid-area: icon;
		user-select: none;

		@container (width <= 27.5rem) {
			align-self: start;
			margin-top: -0.1rem;
		}
	}

	.content {
		font-family: ${vars(tokens.font_family_body)};
		font-size: ${vars(tokens.text_size_body_default)};
		font-weight: ${vars(tokens.font_weight_regular)};
		line-height: ${vars(tokens.line_height_body_default)};
		text-wrap: pretty;
		grid-area: content;
		color: ${vars(locals.color)};

		.heading {
			${define(heading, {
				color: vars(locals.color),
				font_size: vars(tokens.text_size_heading_level_5),
				font_weight: vars(tokens.font_weight_bold),
				line_height: vars(tokens.line_height_heading_level_5),
				text_wrap: 'pretty'
			})};
			-webkit-font-smoothing: antialiased;
			margin-block: 0;
		}
	}

	.body {
		margin: 0;
	}

	.content + ::slotted(lfds-button) {
		${define(button, { width: '100%' })};
		${define(button, { color: vars(locals.color) })};
		${define(button, { border_color: vars(locals.color) })};
		${define(button, { color_hover: vars(locals.color) })};
		${define(button, { border_color_hover: vars(locals.color) })};

		grid-area: button;

		@container (width <= 27.5rem) {
			margin-block-start: ${vars(tokens.spacing_lg)};
		}
	}

	.sr-only {
		position: absolute;
		width: 1px;
		height: 1px;
		margin: -1px;
		padding: 0;
		overflow: hidden;
		clip: rect(0 0 0 0);
		clip-path: inset(50%);
		white-space: nowrap;
		border: 0;
	}

	.close {
		grid-area: close;
		cursor: pointer;
		border: 0;
		background: transparent;
		appearance: none;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: ${vars(tokens.spacing_sm)};
		margin-inline: -0.25rem;
		border-radius: ${vars(tokens.border_radius_sm)};

		&:focus {
			outline: 0;
		}

		&.focus-visible:focus-visible {
			outline: var(--focus-outline);
		}

		.icon-container {
			padding: ${vars(tokens.spacing_sm)};
			border-radius: ${vars(tokens.border_radius_round)};
		}

		&:hover > .icon-container {
			background-color: color-mix(
				in srgb,
				${vars(tokens.color_state_layer_on_light)}
					calc(${vars(tokens.effect_opacity_state_pressed)} * 100%),
				transparent
			);
		}

		@container (width <= 27.5rem) {
			align-self: start;
			margin-top: -1rem;
			margin-bottom: -1rem;
			margin-right: -1rem;
		}
	}
`
